import { format } from 'date-fns';
import type { ReactNode } from 'react';
import {
  LOGO_DATA_URL,
  formatFullName,
  formatGender,
  formatAddress,
  formatLongDate,
  formatPhone,
  getTimezone,
  type TemplatePatient,
} from './templateUtils';

export const TemplateHeader = () => (
  <header className="flex items-center justify-between border-b border-zinc-300 px-6 pt-6 pb-3">
    <div className="flex items-end gap-1.5 text-[11px] leading-tight">
      <span className="font-black tracking-[0.09px]">
        Confidential Medical Data
      </span>
      <span className="font-normal">
        as reported by Health Information Exchanges
      </span>
    </div>
    <img src={LOGO_DATA_URL} alt="Predoc" className="h-5 w-auto" />
  </header>
);

export const PatientDetailsSection = ({
  patient,
}: {
  patient?: TemplatePatient;
}) => (
  <div className="flex flex-col gap-4 rounded-sm border border-slate-200 bg-white py-4">
    <div className="flex flex-col gap-2">
      <p className="px-3 text-[11px] leading-tight font-bold text-zinc-600">
        Patient Details
      </p>
      <div className="h-px w-full bg-zinc-300" />
    </div>

    <dl className="flex flex-col gap-4 px-6 text-[11px] leading-tight">
      <div className="flex gap-2">
        <div className="flex flex-1 flex-col gap-1">
          <dt className="font-bold">Name</dt>
          <dd>{formatFullName(patient)}</dd>
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <dt className="font-bold">Date of birth</dt>
          <dd>{formatLongDate(patient?.dateOfBirth)}</dd>
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <dt className="font-bold">Gender</dt>
          <dd>{formatGender(patient?.genderName)}</dd>
        </div>
      </div>

      <div className="flex gap-2">
        <div className="flex flex-1 flex-col gap-1">
          <dt className="font-bold">Address</dt>
          <dd>{formatAddress(patient?.address)}</dd>
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <dt className="font-bold">Phones</dt>
          <dd>{formatPhone(patient?.contact)}</dd>
        </div>
      </div>
    </dl>
  </div>
);

export const TemplateFooter = ({
  filename,
  generatedAt,
  pageInfo,
}: {
  filename?: string;
  generatedAt: Date;
  pageInfo?: { current: number; total: number };
}) => (
  <footer className="mt-auto flex items-center justify-between border-t border-zinc-300 bg-white pt-4 pr-6 pb-4 pl-6">
    <p className="text-[10px] leading-tight text-zinc-700">{filename ?? ''}</p>
    <div className="flex items-center gap-3">
      <p className="text-[10px] leading-tight whitespace-nowrap text-black">
        Data rendered on {format(generatedAt, 'MMMM d, yyyy')} at{' '}
        {format(generatedAt, 'h:mma')} {getTimezone(generatedAt)} by predoc.ai
      </p>
      {pageInfo ? (
        <>
          <span className="text-[10px] leading-tight text-zinc-700">|</span>
          <p className="text-[10px] leading-tight whitespace-nowrap text-zinc-700">
            Page {pageInfo.current} of {pageInfo.total}
          </p>
        </>
      ) : null}
    </div>
  </footer>
);

export const TemplateEmptyState = ({ message }: { message: string }) => (
  <div className="rounded-sm border-2 border-gray-200 bg-white px-5 py-12 text-center text-[12px] text-zinc-500">
    {message}
  </div>
);

export interface DocumentTemplateShellProps {
  title: string;
  patient?: TemplatePatient;
  filename?: string;
  generatedAt?: Date;
  pageInfo?: { current: number; total: number };
  headerBg?: 'gray' | 'slate';
  reportDate?: string;
  reportDateClassName?: string;
  titleClassName?: string;
  headerClassName?: string;
  children: ReactNode;
}

export const DocumentTemplateShell = ({
  title,
  patient,
  filename,
  generatedAt,
  pageInfo,
  headerBg = 'gray',
  reportDate,
  reportDateClassName = 'text-[12px] leading-tight font-medium text-black',
  titleClassName = 'text-[28px] leading-tight font-bold text-black',
  headerClassName,
  children,
}: DocumentTemplateShellProps) => {
  const generated = generatedAt ?? new Date();
  const headerBgClass = headerBg === 'slate' ? 'bg-slate-100' : 'bg-gray-100';

  return (
    <div className="mx-auto flex min-h-[1056px] w-[816px] flex-col bg-white font-sans text-black">
      <TemplateHeader />

      <section
        className={`flex flex-col gap-4 px-8 pt-8 pb-5 ${headerBgClass} ${headerClassName ?? ''}`}
      >
        {reportDate ? (
          <div className="flex items-center justify-between gap-4">
            <h1 className={titleClassName}>{title}</h1>
            <p className={reportDateClassName}>{reportDate}</p>
          </div>
        ) : (
          <h1 className={titleClassName}>{title}</h1>
        )}
        <PatientDetailsSection patient={patient} />
      </section>

      {children}

      <TemplateFooter
        filename={filename}
        generatedAt={generated}
        pageInfo={pageInfo}
      />
    </div>
  );
};
